import { Box, Text, ThemeContext } from '@vouched.id/vault';
import React, { useContext, useMemo } from 'react';
import { Tooltips } from '../../tooltips';
import { itemHasErrorOrWarning, StatusColors } from '../../utils';
import { Dropdown } from '../Dropdown';
import { DropdownItemsSection } from '../DropdownItemsSection';
import { addIconsToDropdownItems, DropdownItemTypes, DropdownStatusIcon } from '../utils';
import { CrossCheckCaption } from './CrossCheckCaption';
import { CrossCheckDetailsTables } from './CrossCheckDetailsTables';
import { LikelihoodGenuineScore, NameLinkageWarning } from './CrossCheckStrictNameLinkage';
const IdentityScoreItem = ({ score, text, hasWarning }) => {
    const scoreColor = hasWarning ? StatusColors.Warning : StatusColors.Approved;
    const { tokens } = useContext(ThemeContext).theme;
    const fontStyles = {
        fontWeight: tokens.text.weight.bold,
        fontSize: tokens.text.size.medium
    };
    return (React.createElement(Box, { alignItems: "center", display: "flex", style: {
            gap: '10px',
            marginY: '30px'
        } },
        React.createElement(Text, { style: fontStyles }, text),
        React.createElement(Text, { style: {
                color: scoreColor,
                ...fontStyles
            } },
            Math.trunc(score * 100),
            "%")));
};
export const CrossCheckCompletedResult = ({ crossCheckResult, jobErrors, jobStatus, crossCheckSubmittedData }) => {
    const { address, email, phone, ip, confidences, nameLinked } = crossCheckResult;
    const { identity: identityScore, modelIdentity } = confidences;
    const addressIsValid = address?.isValid;
    const addressIsMatch = address?.isMatch;
    const phoneIsValid = phone?.isValid;
    const phoneIsMatch = phone?.isMatch;
    const emailIsValid = email?.isValid;
    const emailIsMatch = email?.isMatch;
    const hasCrosscheckIdentityError = itemHasErrorOrWarning(jobErrors, 'CrosscheckIdentityError').hasWarning;
    const { dropdownItems } = useMemo(() => {
        /*
         * hasErrorOrWarning object for CrossCheck items (except Identity Score) is calculated here b/c they've never
         * existed on the job, it's just visual for now since it does not affect the overall job status
         */
        const dropdownItemsWithoutIcons = [
            {
                text: 'Identity Score',
                type: DropdownItemTypes.ConfidenceDropdownItem,
                available: typeof identityScore === 'number',
                hasErrorOrWarning: {
                    hasError: false,
                    hasWarning: hasCrosscheckIdentityError
                }
            },
            {
                text: 'Address Valid',
                type: DropdownItemTypes.BooleanDropdownItem,
                value: !!addressIsValid,
                available: true,
                hasErrorOrWarning: {
                    hasError: false,
                    hasWarning: !addressIsValid
                },
                tooltip: Tooltips.AddressValid
            },
            {
                text: 'Address Match',
                type: DropdownItemTypes.BooleanDropdownItem,
                value: !!addressIsMatch,
                available: true,
                hasErrorOrWarning: {
                    hasError: false,
                    hasWarning: !addressIsMatch
                },
                tooltip: Tooltips.AddressMatch
            },
            {
                text: 'Phone Number Valid',
                type: DropdownItemTypes.BooleanDropdownItem,
                value: !!phoneIsValid,
                available: true,
                hasErrorOrWarning: {
                    hasError: false,
                    hasWarning: !phoneIsValid
                },
                tooltip: Tooltips.PhoneNumberValid
            },
            {
                text: 'Phone Number Match',
                type: DropdownItemTypes.BooleanDropdownItem,
                value: !!phoneIsMatch,
                available: true,
                hasErrorOrWarning: {
                    hasError: false,
                    hasWarning: !phoneIsMatch
                },
                tooltip: Tooltips.PhoneNumberMatch
            },
            {
                text: 'Email Valid',
                type: DropdownItemTypes.BooleanDropdownItem,
                value: !!emailIsValid,
                available: true,
                hasErrorOrWarning: {
                    hasError: false,
                    hasWarning: !emailIsValid
                },
                tooltip: Tooltips.EmailValid
            },
            {
                text: 'Email Match',
                type: DropdownItemTypes.BooleanDropdownItem,
                value: !!emailIsMatch,
                available: true,
                hasErrorOrWarning: {
                    hasError: false,
                    hasWarning: !emailIsMatch
                },
                tooltip: Tooltips.EmailMatch
            }
        ];
        if (ip) {
            const ipIsValid = ip.isValid;
            const ipDistance = ip.distance;
            dropdownItemsWithoutIcons.push({
                text: 'IP Valid',
                type: DropdownItemTypes.BooleanDropdownItem,
                value: ipIsValid,
                available: true,
                hasErrorOrWarning: {
                    hasError: false,
                    hasWarning: !ipIsValid
                },
                tooltip: Tooltips.IpValid
            }, {
                text: 'IP Distance',
                type: DropdownItemTypes.BooleanDropdownItem,
                value: typeof ipDistance === 'number' && ipDistance <= 50,
                available: true,
                hasErrorOrWarning: {
                    hasError: false,
                    hasWarning: typeof ipDistance !== 'number' || ipDistance > 50
                },
                tooltip: Tooltips.IpDistance
            });
        }
        return addIconsToDropdownItems(dropdownItemsWithoutIcons);
    }, [
        identityScore,
        addressIsValid,
        addressIsMatch,
        phoneIsValid,
        phoneIsMatch,
        emailIsValid,
        emailIsMatch,
        ip,
        hasCrosscheckIdentityError
    ]);
    return (React.createElement(Dropdown, { statusIcon: React.createElement(DropdownStatusIcon, { dropdownHasRejections: false, dropdownHasWarnings: hasCrosscheckIdentityError, parentJobStatus: jobStatus }), title: "CrossCheck" },
        React.createElement(Box, null,
            React.createElement(CrossCheckCaption, null),
            React.createElement(IdentityScoreItem, { hasWarning: hasCrosscheckIdentityError, score: identityScore, text: "Identity Score" }),
            nameLinked === false && React.createElement(NameLinkageWarning, null),
            nameLinked === false && typeof modelIdentity === 'number' && (React.createElement(LikelihoodGenuineScore, { score: modelIdentity })),
            crossCheckResult.model ? null : (React.createElement(DropdownItemsSection, { dropdownItems: dropdownItems.filter((item) => item.text !== 'Identity Score') })),
            React.createElement(CrossCheckDetailsTables, { address: address, email: email, ip: ip, phone: phone, submittedData: crossCheckSubmittedData }))));
};
